/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { Modal, Row, Col, Card, Container } from 'react-bootstrap';
import { FormTextArea, FormDatePicker, FormSelect } from '@awp/components/Common/AwpFormInputs';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';

import { PutJson } from '@awp/helpers/fetchReact';
import { REST_API, Permissions, FORM_MODE, DATASET_TYPES, FROST_PROBE_CONFIG } from '@awp/constants';
import AwpYesNoDialog from '@awp/components/Common/AwpYesNoDialog';
import AwpFormActionButtons from '@awp/components/Common/AwpFormActionButtons';
import ValidationDataTypeView from '@components/ManualValidation/validationDataTypeView';

import { format as formatDate } from 'date-fns';
import {
  isInt,
  isValueEmpty,
  sortFrostProbeTypeData,
  getFrostProbeTypeCombinedName,
  convertRelElevationTocm,
} from '@awp/helpers/utilsReact';

const entityName = 'Manual Validation';
const defaultChangeReason = { value: 'manualValidation', label: 'Manual Validation' };
const changeReasons = [defaultChangeReason];
function ValidationModal({ weatherStation, startDate, endDate, dataSets, setMode, mode, postValidationSubmit }) {
  // eslint-disable-next-line no-unused-vars
  const [entityErrors, setEntityErrors] = useState(null);
  const [manuValidData, setManuValidData] = useState({});
  const [dataTypes, setDataTypes] = useState([]);
  const [segmentObservationTypes, setSegmentObservationTypes] = useState([]);
  const [showCancelConfirm, setShowCancelConfirm] = React.useState(false);
  const isClickedView = React.useState(false);
  const readOnly = false;

  useEffect(() => {
    if (!dataSets) return;
    setManuValidData({
      ReasonForChange: defaultChangeReason,
      Comments: '',
    });

    getDataTypes();
  }, [dataSets]);

  const getDataTypes = () => {
    if (!dataSets || dataSets.length === 0) return [];
    let _data = [...(dataSets || [])].filter((e) => e.type !== DATASET_TYPES.Image);
    let cols = [];
    _data.forEach((ds) => {
      if (!isValueEmpty(ds.rawlabel)) {
        let col = {};
        col.DisplayName = ds.rawlabel + '_' + ds.PlacementName;
        col.DatasetId = ds.DatasetId; //DatasetId: [DATASET_PREFIX]_[OBSERVATION_TYPE_ID]_[SENSOR_PLACEMENT_ID |CAMERA_ID | WEATHER_FORECAST_TYPE_ID]
        //process exemptional case for pavement 0 depth of frost probe
        col.SensorLayerNumber =
          ds.type === FROST_PROBE_CONFIG.FORST_PROBE_SENSOR_TYPE &&
          ds.SensorTypeName === FROST_PROBE_CONFIG.PAVEMENT_TEMP &&
          ds.SensorLayerNumber === 0
            ? null
            : ds.SensorLayerNumber;
        col.SortName = col.DisplayName;
        //need relative elevation for proper pole sorting it's ok to be null
        col.RelativeElevation = ds.RelativeElevation;

        //frost probe/snow pole
        if (!isValueEmpty(ds.SensorLayerNumber)) {
          if (isValueEmpty(ds.RelativeElevation)) {
            col.DisplayName = col.DisplayName + '_' + ds.SensorLayerNumber;
          } else {
            col.DisplayName = getFrostProbeTypeCombinedName(
              col.DisplayName,
              convertRelElevationTocm(ds.RelativeElevation) ?? '',
            );
          }
        }
        cols.push(col);
      }
    });

    setDataTypes(sortFrostProbeTypeData(cols, 'SortName'));
  };

  const handleSegmentObservationTypes = (segmentObservationType) => {
    let currentSegmentObservationTypes = [...segmentObservationTypes];
    let newSots = [];
    let isFound = false;
    currentSegmentObservationTypes.map((sot) => {
      if (
        sot.ObservationTypeId === segmentObservationType.ObservationTypeId &&
        sot.SensorPlacementId === segmentObservationType.SensorPlacementId &&
        sot.SensorLayerNumber === segmentObservationType.SensorLayerNumber
      ) {
        if (!isValueEmpty(segmentObservationType.IsValid)) newSots.push(segmentObservationType);
        isFound = true;
      } else {
        newSots.push(sot);
      }
    });
    if (!isFound) newSots.push(segmentObservationType);
    setSegmentObservationTypes(newSots);
  };

  const handleCancel = () => {
    setMode(FORM_MODE.LIST);
  };
  const getValidationHist = (props) => {
    let sd = startDate;
    let ed = endDate;
    if (props && props.values) {
      let values = props.values;
      if (values.mvstartdate) sd = values.mvstartdate;
      if (values.mvenddate) ed = values.mvenddate;
    }
    return {
      From: formatDate(sd, 'yyyy-MM-dd-HH-mm'),
      To: formatDate(ed, 'yyyy-MM-dd-HH-mm'),
      ObservationTypeId: '',
      SensorPlacementId: '',
    };
  };
  const updateManuValidation = (setSubmitting, resetForm, setErrors, manuValidDto) => {
    const url = REST_API.OBSERVATIONS_VALIDATION;
    PutJson(url, manuValidDto)
      .then(async (response) => {
        if (response.ok) {
          const manu = await response.json();
          if (manu) {
            toast.success('Data validation successfully changed!');
          } else {
            toast.info('No data validation changed.');
          }
          setMode(FORM_MODE.LIST);
          setSegmentObservationTypes([]);
          postValidationSubmit(manu);
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
          if (errors.entity) setEntityErrors({ Datavalidation: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  // eslint-disable-next-line no-unused-vars
  const handleSubmit = (values, { setSubmitting, resetForm, setErrors, setFieldError }) => {
    setSubmitting(true);
    const manuValidDto = {
      ...manuValidData,
      From: formatDate(values.mvstartdate, 'yyyy-MM-dd-HH-mm'),
      To: formatDate(values.mvenddate, 'yyyy-MM-dd-HH-mm'),
      SegmentObservationTypes: segmentObservationTypes,
      ReasonForChange: values.reasonforchange.value,
      Comments: values.comments,
    };
    if (mode === FORM_MODE.EDIT) {
      updateManuValidation(setSubmitting, resetForm, setErrors, manuValidDto);
    }
  };
  const changeClickedView = (props) => {
    props.setFieldValue('isclickedview', true);
  };
  return (
    <Modal size="lg" show={mode === FORM_MODE.EDIT} onHide={handleCancel} backdrop="static">
      <Formik
        initialValues={{
          reasonforchange: manuValidData.ReasonForChange ?? defaultChangeReason,
          comments: manuValidData.Comments ?? '',
          mvstartdate: isInt(startDate) ? new Date(startDate) : startDate,
          mvenddate: isInt(endDate) ? new Date(endDate) : endDate,
          isclickedview: isClickedView,
        }}
        validateOnMount={true}
        validationSchema={Yup.object({
          mvstartdate: Yup.date()
            .nullable()
            .required('Start date is required')
            .test('checkstart', function () {
              if (this.parent.mvstartdate > this.parent.mvenddate) {
                return this.createError({
                  message: 'Start date must be before end date',
                  path: 'mvstartdate',
                });
              }
              return true;
            }),
          mvenddate: Yup.date()
            .nullable()
            .required('End date is required')
            .test('checkend', function () {
              if (this.parent.mvenddate < this.parent.mvstartdate) {
                return this.createError({
                  message: 'End date must be after start date',
                  path: 'mvenddate',
                });
              }
              return true;
            }),
          comments: Yup.string().max(4000, 'Maximum 4000 characters are allowed'),
        })}
        onSubmit={handleSubmit}
      >
        {(props) => (
          <Form>
            <Modal.Header>
              <Modal.Title>
                <h2>Apply Manual Data Validation</h2>
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Row className="pb-3">
                <Col md={3}>
                  <b>Weather station:</b>
                </Col>
                <Col md={9}>{weatherStation.Name}</Col>
              </Row>

              <Card>
                <Card.Header className="bg-transparent">
                  <Row className="mt-3">
                    <Col md={5}>
                      <FormDatePicker
                        readOnly={readOnly}
                        label="Start Date"
                        id="mvstartdate"
                        name="mvstartdate"
                        showTimeSelect={true}
                        showTime={{ isEnabled: true }}
                        timeFormat="HH:mm"
                        timeIntervals={60}
                        dateFormat={['yyyy-MM-dd HH:mm']}
                      />
                    </Col>
                    <Col md={5}>
                      <FormDatePicker
                        readOnly={readOnly}
                        label="End Date"
                        id="mvenddate"
                        name="mvenddate"
                        showTimeSelect={true}
                        showTime={{ isEnabled: true }}
                        timeFormat="HH:mm"
                        timeIntervals={60}
                        dateFormat={['yyyy-MM-dd HH:mm']}
                      />
                    </Col>
                  </Row>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col sm={6} className="mb-4">
                      <FormSelect
                        readOnly={readOnly}
                        label="Reason for change (required)"
                        id="reasonforchange"
                        name="reasonforchange"
                        //textValue={weatherStationData.StatusTypeName}
                        options={changeReasons}
                        isMulti={false}
                      />
                    </Col>
                    <Col sm={6} className="mb-4">
                      <FormTextArea
                        readOnly={readOnly}
                        label="Comments (optional)"
                        instruction="(4,000 Characters max)"
                        id="comments"
                        name="comments"
                        rows={3}
                      />
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Row style={{ height: '1.25rem' }}></Row>
              <Card>
                <Row
                  className="font-weight-bold"
                  style={{ fontSize: '12px', marginLeft: '0', marginRight: '0', marginTop: '1em', marginBottom: '1em' }}
                >
                  <Col sm={1}></Col>
                  <Col sm={4}>Data Type</Col>
                  <Col sm={3}>No Change</Col>
                  <Col sm={2}>Valid</Col>
                  <Col sm={2}>Invalid</Col>
                </Row>
              </Card>
              <Card>
                <div className="overflow-auto" style={{ height: '240px', resize: 'vertical', maxHeight: '400px' }}>
                  {isValueEmpty(dataTypes) || dataTypes.length === 0
                    ? ''
                    : // eslint-disable-next-line no-unused-vars
                      dataTypes.map((dt, i) => {
                        if (isValueEmpty(dt) || isValueEmpty(dt.DatasetId)) return '';
                        return (
                          <ValidationDataTypeView
                            selectedDataSet={dt}
                            key={'validationDataTypeView_' + dt.DatasetId}
                            handleSegmentObservationTypes={handleSegmentObservationTypes}
                            getValidationHist={() => getValidationHist(props)}
                            setIsClickedView={() => changeClickedView(props)}
                          />
                        );
                      })}
                </div>
              </Card>
            </Modal.Body>
            <Modal.Footer style={{ paddingTop: '0rem', paddingBottom: '0rem' }}>
              <Container>
                {!showCancelConfirm ? (
                  <Row className="my-2">
                    <AwpFormActionButtons
                      title=" "
                      entityName={entityName}
                      buttonLabel={'Save'}
                      mode={mode}
                      setMode={setMode}
                      permissions={[Permissions.WriteDataTableManualValidation]}
                      readOnly={readOnly}
                      showDeleteBtn={false}
                      btnCancelName={'Close'}
                      props={props}
                      isPristine={!props.dirty || props.isSubmitting}
                    />
                  </Row>
                ) : (
                  <Row className="my-2">
                    <AwpYesNoDialog
                      action={updateManuValidation}
                      setShowYesNoConfirm={setShowCancelConfirm}
                      props={props}
                    />
                  </Row>
                )}
              </Container>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}

export default ValidationModal;
